package horcrux.frontend.common

import horcrux.common.ValidatedValueClass
import horcrux.common.api.dtos.PostRawDocumentsErrorType
import horcrux.common.api.dtos.RawDocumentClientSideID
import horcrux.common.api.dtos.RawDocumentMetadata
import horcrux.common.api.dtos.types.DocumentInput
import horcrux.common.api.dtos.types.DocumentName
import horcrux.common.api.dtos.types.RawDocumentName
import horcrux.common.api.dtos.types.UploadedDocumentIdentifier
import horcrux.common.types.NonEmptyMap
import horcrux.frontend.common.views.collections.importdocuments.DocumentInputMaybe
import horcrux.frontend.common.views.collections.importdocuments.DocumentURLMaybe
import io.ktor.utils.io.core.toByteArray
import js.buffer.ArrayBuffer
import js.promise.await
import js.typedarrays.Int8Array
import kotlinx.serialization.Serializable
import web.file.File

open class HorcruxClientError(causeMaybe: Throwable? = null) : Throwable(causeMaybe)

sealed interface LocalDocumentInput : DocumentInput {
    val rawDocumentName: RawDocumentName
    suspend fun toByteArray(): ByteArray
    override val defaultDocumentName: DocumentName
        get() = DocumentName.truncate(rawDocumentName.value)
}

fun ArrayBuffer.toByteArray(): ByteArray = Int8Array(this).unsafeCast<ByteArray>()

@Serializable
data class DocumentText(
    override val value: String,
    override val rawDocumentName: RawDocumentName,
) :
    ValidatedValueClass<String, DocumentText>,
    LocalDocumentInput {
    constructor(value: String) : this(value, RawDocumentName(value))

    override suspend fun toByteArray(): ByteArray =
        value.toByteArray()

    fun isEmpty(): Boolean = value.isEmpty()
}

class DocumentByteArray(
    override val value: ByteArray,
    override val rawDocumentName: RawDocumentName
) : ValidatedValueClass<ByteArray, DocumentByteArray>, LocalDocumentInput {
    override suspend fun toByteArray(): ByteArray = value

}

data class DocumentFile(override val value: File) :
    ValidatedValueClass<File, DocumentFile>, LocalDocumentInput {

    override val rawDocumentName: RawDocumentName
        get() = RawDocumentName(value.name)

    override suspend fun toByteArray(): ByteArray =
        value.arrayBuffer().await().toByteArray()

    companion object
}

data class DocumentInputData(
    val rawDocumentClientSideID: RawDocumentClientSideID,
    val documentInputMaybe: DocumentInputMaybe<out DocumentInput>,
    val documentName: DocumentName,
    val rawDocumentMetadataMaybe: RawDocumentMetadata?,
    val postRawDocumentsErrorTypeMaybe: PostRawDocumentsErrorType?
) {
    constructor() : this(DocumentURLMaybe().also { it.defaultDocumentName })

    constructor(
        documentInputMaybe: DocumentInputMaybe<out DocumentInput>
    ) : this(
        RawDocumentClientSideID(0),
        documentInputMaybe,
        null,
    )

    constructor(
        rawDocumentClientSideID: RawDocumentClientSideID,
        documentInputMaybe: DocumentInputMaybe<out DocumentInput>
    ) : this(
        rawDocumentClientSideID,
        documentInputMaybe,
        null,
    )

    constructor(
        rawDocumentClientSideID: RawDocumentClientSideID,
        documentInputMaybe: DocumentInputMaybe<out DocumentInput>,
        rawDocumentMetadata: RawDocumentMetadata?
    ) : this(
        rawDocumentClientSideID,
        documentInputMaybe,
        documentInputMaybe.defaultDocumentName,
        rawDocumentMetadata,
        null
    )

    val documentIdentifierMaybe: UploadedDocumentIdentifier? = rawDocumentMetadataMaybe?.let {
        UploadedDocumentIdentifier(rawDocumentID = it.rawDocumentID, documentName = documentName)
    }

    val isInvalid: Boolean
        get() = documentInputMaybe.isInvalid() || postRawDocumentsErrorTypeMaybe != null
    val errorMessage: String = when {
        documentInputMaybe is DocumentURLMaybe && documentInputMaybe.isInvalid() -> "URL is invalid"
        postRawDocumentsErrorTypeMaybe != null -> postRawDocumentsErrorTypeMaybe.errorMessage
        else -> ""
    }

    fun withDocumentInputMaybe(documentInputMaybe: DocumentInputMaybe<out DocumentInput>): DocumentInputData =
        this.copy(documentInputMaybe = documentInputMaybe, documentName = documentInputMaybe.defaultDocumentName)
}

typealias ViewRefreshState = BooleanState
typealias DocumentInputDataByClientSideIDState = State<NonEmptyMap<RawDocumentClientSideID, DocumentInputData>>

interface HasViewName {
    val viewName: String
}
